import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../ui/button";
import { Lock, Scale, Camera, Zap, Cloud, Database, Upload, CreditCard } from "lucide-react";
import { useState } from "react";
import { useToast } from "../../hooks/use-toast";
import { getAuthToken, getTenantId } from "../../lib/auth";

export default function SystemStatus() {
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const { toast } = useToast();

  // Fetch system status from API
  const { data: systemStatus, isLoading: statusLoading, error } = useQuery({
    queryKey: ["/api/system/status"],
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const handleOpenGates = async () => {
    setIsLoading("gates");
    try {
      const token = getAuthToken();
      const tenantId = getTenantId();
      
      const response = await fetch("/api/gates/open-all", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "X-Tenant-Id": tenantId || ''
        }
      });

      if (response.ok) {
        toast({
          title: "🚪 Gates Opened",
          description: "All weighbridge gates have been opened successfully",
          variant: "default"
        });
      } else {
        throw new Error("Failed to open gates");
      }
    } catch (error) {
      toast({
        title: "Gate Control Error",
        description: "Failed to open gates. Please check system status.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(null);
    }
  };

  const handleCalibrateScales = async () => {
    setIsLoading("scales");
    try {
      const token = getAuthToken();
      const tenantId = getTenantId();
      
      const response = await fetch("/api/scales/calibrate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "X-Tenant-Id": tenantId || ''
        }
      });

      if (response.ok) {
        toast({
          title: "⚖️ Scale Calibration Started",
          description: "All scales are now calibrating. This may take a few minutes.",
          variant: "default"
        });
      } else {
        throw new Error("Failed to calibrate scales");
      }
    } catch (error) {
      toast({
        title: "Calibration Error",
        description: "Failed to start scale calibration. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(null);
    }
  };

  const handleExportReport = async () => {
    setIsLoading("report");
    try {
      const token = getAuthToken();
      const tenantId = getTenantId();
      
      const response = await fetch("/api/visits/export", {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "X-Tenant-Id": tenantId || ''
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `visits-export-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast({
          title: "📊 Excel Report Exported",
          description: "Daily visits report has been exported to Excel",
          variant: "default"
        });
      } else {
        throw new Error("Failed to export report");
      }
    } catch (error) {
      toast({
        title: "Export Error",
        description: "Failed to export Excel report. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(null);
    }
  };

  // Get icon for equipment type
  const getEquipmentIcon = (type: string) => {
    switch (type) {
      case 'scale': return Scale;
      case 'camera': return Camera;
      case 'gate': return Lock;
      default: return Scale;
    }
  };

  // Get icon for integration
  const getIntegrationIcon = (name: string) => {
    if (name.includes('Auth')) return Lock;
    if (name.includes('Orders')) return Database;
    if (name.includes('Media')) return Upload;
    if (name.includes('Billing')) return CreditCard;
    return Database;
  };

  // Process equipment status from API
  const equipmentStatus = systemStatus?.equipment?.map(equipment => ({
    name: equipment.name,
    description: equipment.description,
    status: equipment.status,
    icon: getEquipmentIcon(equipment.type)
  })) || [];

  // Process integration status from API
  const integrationStatus = systemStatus?.integrations?.map(integration => ({
    name: integration.name,
    status: integration.status,
    icon: getIntegrationIcon(integration.name)
  })) || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "online":
        return "text-green-600";
      case "maintenance":
        return "text-yellow-600";
      case "offline":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getStatusIndicator = (status: string) => {
    switch (status) {
      case "online":
        return "status-online";
      case "maintenance":
        return "status-warning";
      case "offline":
        return "status-offline";
      default:
        return "status-offline";
    }
  };

  return (
    <div className="space-y-6">
      {/* Equipment Status */}
      <div className="bg-card rounded-xl border border-border shadow-sm p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Equipment Status</h3>
        
        {statusLoading ? (
          <div className="text-center py-4" data-testid="loading-system-status">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2 text-sm text-muted-foreground">Loading system status...</p>
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-600" data-testid="error-system-status">
            <p className="text-sm">Failed to load system status</p>
          </div>
        ) : (
          <div className="space-y-4">
            {equipmentStatus.map((equipment, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center">
                  <span className={`status-indicator ${getStatusIndicator(equipment.status)}`}></span>
                  <div>
                    <div className="text-sm font-medium text-foreground" data-testid={`equipment-${equipment.name.toLowerCase().replace(/\s+/g, '-')}`}>
                      {equipment.name}
                    </div>
                    <div className="text-xs text-muted-foreground">{equipment.description}</div>
                  </div>
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded ${
                  equipment.status === "online" 
                    ? "text-green-600 bg-green-50" 
                    : equipment.status === "maintenance"
                    ? "text-yellow-600 bg-yellow-50"
                    : "text-red-600 bg-red-50"
                }`}>
                  {equipment.status === "online" ? "Online" : equipment.status === "maintenance" ? "Maintenance" : "Offline"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Integration Status */}
      <div className="bg-card rounded-xl border border-border shadow-sm p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Navig8 Integration</h3>
        
        {statusLoading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2 text-sm text-muted-foreground">Loading integration status...</p>
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-600">
            <p className="text-sm">Failed to load integration status</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {integrationStatus.map((service, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className={`status-indicator ${getStatusIndicator(service.status)}`}></span>
                    <span className="text-sm text-foreground" data-testid={`service-${service.name.toLowerCase().replace(/\s+/g, '-').replace('/', '-')}`}>
                      {service.name}
                    </span>
                  </div>
                  <span className={`text-xs ${getStatusColor(service.status)}`}>
                    {service.status === "online" ? "Connected" : "Disconnected"}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-xs text-muted-foreground">
                Last sync: {systemStatus?.integrations?.[0]?.lastSync ? 
                  new Date(systemStatus.integrations[0].lastSync).toLocaleTimeString() : 
                  "Recently"}
              </div>
              <div className="text-xs text-muted-foreground">API Health: 99.8% uptime</div>
            </div>
          </>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-card rounded-xl border border-border shadow-sm p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Button 
            className="justify-center bg-accent text-accent-foreground hover:bg-accent/90 flex-col h-20" 
            onClick={handleOpenGates}
            disabled={isLoading !== null}
            data-testid="button-open-gates"
          >
            <Lock className="w-5 h-5 mb-2" />
            <span className="text-sm font-medium">
              {isLoading === "gates" ? "Opening..." : "Open All Gates"}
            </span>
          </Button>

          <Button 
            className="justify-center bg-primary text-primary-foreground hover:bg-primary/90 flex-col h-20"
            onClick={handleCalibrateScales}
            disabled={isLoading !== null}
            data-testid="button-calibrate-scales"
          >
            <Scale className="w-5 h-5 mb-2" />
            <span className="text-sm font-medium">
              {isLoading === "scales" ? "Calibrating..." : "Calibrate Scales"}
            </span>
          </Button>

          <Button 
            variant="secondary" 
            className="justify-center flex-col h-20"
            onClick={handleExportReport}
            disabled={isLoading !== null}
            data-testid="button-export-report"
          >
            <Zap className="w-5 h-5 mb-2" />
            <span className="text-sm font-medium">
              {isLoading === "report" ? "Exporting..." : "Export Daily Report"}
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
}
